import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Menu, X, Phone, ChevronDown, Home as HomeIcon, BookOpen, Layers, Compass,
  Info, Newspaper, Mail, MapPin, Laptop, GraduationCap, Trophy, MessageSquare,
  Computer, ListChecks, UserPlus, IndianRupee, HelpCircle, ScrollText, LogIn, ArrowRight,
} from "lucide-react";
import { SITE } from "@/data/site";
import { Button } from "@/components/ui/button";
import TutorCtaLink from "@/components/TutorCtaLink";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/", label: "Home", Icon: HomeIcon },
  { to: "/courses", label: "Courses", Icon: BookOpen },
  { to: "/boards", label: "Boards", Icon: Layers },
  { to: "/how-it-works", label: "How It Works", Icon: Compass },
  { to: "/about", label: "About", Icon: Info },
  { to: "/blog", label: "Blog", Icon: Newspaper },
  { to: "/contact", label: "Contact", Icon: Mail },
];

// Mobile-drawer-only sections
const mobileLocations = [
  { to: "/jabalpur", label: "Jabalpur", Icon: MapPin },
  { to: "/nagpur", label: "Nagpur", Icon: MapPin },
];
const mobileServices = [
  { to: "/home-tuition", label: "Home Tuition", Icon: HomeIcon },
  { to: "/online-classes", label: "Online Classes", Icon: Laptop },
  { to: "/coaching-classes", label: "Coaching Classes", Icon: GraduationCap },
  { to: "/competitive-exams", label: "Competitive Exams (NEET / JEE)", Icon: Trophy },
  { to: "/spoken-english", label: "Spoken English", Icon: MessageSquare },
  { to: "/computer-courses", label: "Computer Courses", Icon: Computer },
  { to: "/grades", label: "All Grades", Icon: ListChecks },
];
const mobileResources = [
  { to: "/pay-now", label: "Pay Now", Icon: IndianRupee },
  { to: "/faq", label: "FAQs", Icon: HelpCircle },
  { to: "/policies/terms", label: "Policies", Icon: ScrollText },
  { to: "/login", label: "Login", Icon: LogIn },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // Close on route change
  useEffect(() => setOpen(false), [location.pathname]);
  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Drawer can ONLY be opened by tapping the hamburger button below.
  // No swipe/gesture handler is attached anywhere — `openMenu`/`closeMenu`
  // are the single source of truth for drawer state.
  const openMenu = () => {
    setOpen(true);
    // Push a transient history state so the Android/browser Back button
    // closes the drawer instead of navigating away from the page.
    try {
      if (typeof window !== "undefined" && !window.history.state?.uhtDrawer) {
        window.history.pushState({ uhtDrawer: true }, "");
      }
    } catch (e) { /* ignore history errors */ }
  };
  const closeMenu = () => setOpen(false);

  // Listen for popstate (Back) — if drawer is open, just close it (the
  // pushState above is what gets popped, so we stay on the current page).
  useEffect(() => {
    if (!open) return;
    const onPop = () => setOpen(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open]);

  return (
    <header className="sticky top-0 z-50 w-full" data-testid="site-header">
      <div className="bg-brand-deep text-white/90 text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-2">
          <span className="hidden sm:inline">📍 Head Office: Ghadi Chowk, Vijay Nagar, Jabalpur • Branch: Dharampeth, Nagpur</span>
          <span className="sm:hidden">📍 Jabalpur • Nagpur</span>
          <div className="flex items-center gap-4">
            <a className="hover:text-brand-cyan" href={`tel:${SITE.jabalpur.phoneRaw}`} data-testid="topbar-call-jbp">Jabalpur: {SITE.jabalpur.phone}</a>
            <a className="hover:text-brand-cyan hidden sm:inline" href={`tel:${SITE.nagpur.phoneRaw}`} data-testid="topbar-call-ngp">Nagpur: {SITE.nagpur.phone}</a>
          </div>
        </div>
      </div>
      <div className="bg-white/85 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 h-[84px] sm:h-24 lg:h-28 flex items-center justify-between">
          <Link to="/" className="flex items-center group shrink-0" data-testid="brand-logo" aria-label="Unique Home Tutors — Home">
            <img
              src={SITE.logo}
              alt="Unique Home Tutors"
              width="800"
              height="674"
              className="uht-logo h-14 sm:h-20 lg:h-24 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              draggable="false"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 text-[13.5px] font-medium tracking-tight rounded-full transition ${
                    isActive ? "text-brand-royal bg-slate-100" : "text-slate-700 hover:text-brand-royal hover:bg-slate-50"
                  }`
                }
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger className="px-3 py-2 text-[13.5px] font-medium tracking-tight rounded-full text-slate-700 hover:text-brand-royal hover:bg-slate-50 flex items-center gap-1" data-testid="nav-cities">
                Cities <ChevronDown size={14} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <DropdownMenuItem asChild><Link to="/jabalpur" data-testid="dropdown-jabalpur">Jabalpur</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/nagpur" data-testid="dropdown-nagpur">Nagpur</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger className="px-3 py-2 text-[13.5px] font-medium tracking-tight rounded-full text-slate-700 hover:text-brand-royal hover:bg-slate-50 flex items-center gap-1" data-testid="nav-more">
                More <ChevronDown size={14} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <DropdownMenuItem asChild><Link to="/home-tuition">Home Tuition</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/coaching-classes">Coaching Classes</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/online-classes">Online Classes</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/competitive-exams">Competitive Exams (NEET/JEE)</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/spoken-english">Spoken English</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/computer-courses">Computer Courses</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/grades">All Grades</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><TutorCtaLink placement="header-menu" data-testid="cta-tutor-header-menu">Become a Tutor</TutorCtaLink></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/pay-now">Pay Now</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/faq">FAQs</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/policies/terms">Policies</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/login">Login</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="hidden md:inline-flex rounded-full border-brand-royal/15 font-semibold text-[13.5px] tracking-tight" data-testid="header-call-btn">
              <a href={`tel:${SITE.jabalpur.phoneRaw}`}><Phone size={14} className="mr-1" /> Call</a>
            </Button>
            <Button asChild className="rounded-full bg-brand-royal hover:bg-brand-royal/90 text-white font-semibold text-[13px] sm:text-[13.5px] tracking-tight btn-shine px-4 sm:px-5 h-9 sm:h-10" data-testid="header-book-demo">
              <Link to="/book-demo">Book Free Demo</Link>
            </Button>
            <button
              type="button"
              className="lg:hidden p-2 -mr-1 rounded-lg hover:bg-slate-100 transition"
              onClick={open ? closeMenu : openMenu}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-menu"
              data-testid="header-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* ===== Mobile drawer ===== */}
        {/* Backdrop — pointer-events-none when closed so it can never absorb touches/clicks */}
        <div
          className={`lg:hidden fixed inset-0 top-0 z-[60] bg-black/55 backdrop-blur-sm transition-opacity duration-300 ${
            open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
          onClick={closeMenu}
          aria-hidden="true"
          data-testid="mobile-menu-backdrop"
        />
        {/* Drawer — inert + pointer-events-none when closed so no edge-swipe can ever reach it */}
        <aside
          id="mobile-menu"
          className={`lg:hidden fixed top-0 right-0 z-[70] h-[100dvh] w-[85vw] max-w-[360px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out will-change-transform ${
            open ? "translate-x-0 pointer-events-auto" : "translate-x-full pointer-events-none"
          }`}
          style={{ touchAction: open ? "pan-y" : "none" }}
          aria-hidden={!open}
          {...(!open ? { inert: "" } : {})}
          data-testid="mobile-menu"
        >
          {/* Drawer header */}
          <div className="flex items-center justify-between px-4 h-[72px] border-b border-slate-100 shrink-0">
            <Link to="/" onClick={closeMenu} className="flex items-center" aria-label="Unique Home Tutors — Home">
              <img src={SITE.logo} alt="UHT" className="uht-logo h-12 w-auto object-contain" />
            </Link>
            <button
              type="button"
              onClick={closeMenu}
              className="p-2 -mr-1 rounded-lg text-slate-600 hover:bg-slate-100 transition"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Scrollable menu body */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-3">
            <MobileSection title="Main Menu">
              {links.map(({ to, label, Icon }) => (
                <MobileItem key={to} to={to} Icon={Icon} label={label} end={to === "/"} testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`} />
              ))}
            </MobileSection>

            <MobileSection title="Locations">
              {mobileLocations.map(({ to, label, Icon }) => (
                <MobileItem key={to} to={to} Icon={Icon} label={label} testid={`mobile-nav-${label.toLowerCase()}`} />
              ))}
            </MobileSection>

            <MobileSection title="Services">
              {mobileServices.map(({ to, label, Icon }) => (
                <MobileItem key={to} to={to} Icon={Icon} label={label} testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`} />
              ))}
              <TutorCtaLink
                placement="header-mobile"
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 hover:text-brand-royal transition"
                data-testid="cta-tutor-header-mobile"
              >
                <UserPlus size={16} className="text-slate-400" />
                Become a Tutor
              </TutorCtaLink>
            </MobileSection>

            <MobileSection title="Resources" last>
              {mobileResources.map(({ to, label, Icon }) => (
                <MobileItem key={to} to={to} Icon={Icon} label={label} testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`} />
              ))}
            </MobileSection>
          </div>

          {/* Sticky CTA footer */}
          <div className="border-t border-slate-100 px-4 py-3 bg-white shrink-0 space-y-2">
            <Button asChild className="w-full rounded-full bg-brand-royal hover:bg-brand-royal/90 text-white font-semibold h-11 btn-shine" data-testid="mobile-book-demo">
              <Link to="/book-demo">Book Free Demo <ArrowRight size={15} className="ml-2" /></Link>
            </Button>
            <a
              href={`tel:${SITE.jabalpur.phoneRaw}`}
              className="flex items-center justify-center gap-2 w-full h-11 rounded-full border border-brand-royal/20 text-brand-royal font-semibold text-sm hover:bg-slate-50 transition"
              data-testid="mobile-call-btn"
            >
              <Phone size={14} /> {SITE.jabalpur.phone}
            </a>
          </div>
        </aside>
      </div>
    </header>
  );
}

// ===== Mobile drawer helpers =====
function MobileSection({ title, last, children }) {
  return (
    <div className={`${last ? "" : "border-b border-slate-100 pb-3 mb-3"}`}>
      <div className="px-3 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
        {title}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function MobileItem({ to, label, Icon, end, testid }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
          isActive
            ? "bg-brand-royal/8 text-brand-royal"
            : "text-slate-700 hover:bg-slate-100 hover:text-brand-royal"
        }`
      }
      data-testid={testid}
    >
      {({ isActive }) => (
        <>
          <Icon size={16} className={isActive ? "text-brand-royal" : "text-slate-400"} />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}
